import { useCallback, useState } from "react";

import { Dialog, DialogContent } from "@mui/material";
import { usePostHog } from "posthog-js/react";
import { useParams } from "react-router-dom";
import { useSWRConfig } from "swr";

import { ShipmentLiveUpdateController } from "components/ShipmentLiveUpdateBanner";
import { CarrierShipmentPageProvider } from "context";
import {
  useRoleContext,
  useShipment,
  useShipmentFiltersContext,
  useShipments,
} from "hooks";
import {
  getActiveFiltersCount,
  getPageCount,
  revalidateShipmentDetailAndListCaches,
} from "utils";

import { AppPage, ClientAppEvent } from "../../../../constants";
import { ShipmentOverviewExpanded } from "../ShipmentOverviewExpanded";
import { ShipmentsFilter } from "../ShipmentsFilter";
import { Shipment } from "./Shipment";

const QUICK_SHIPMENT_BROWSER_PAGE_SIZE = 5;

export function ShipmentContainer() {
  const { mutate: revalidateAll } = useSWRConfig();
  const { role } = useRoleContext();
  const params = useParams();

  const [page, setPage] = useState<number>(1);
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);

  const shipmentId = params.id;
  const {
    shipments,
    isLoading: shipmentsAreLoading,
    count,
  } = useShipments({ page, pageSize: QUICK_SHIPMENT_BROWSER_PAGE_SIZE });
  const pages = getPageCount(count, QUICK_SHIPMENT_BROWSER_PAGE_SIZE);

  const { filters } = useShipmentFiltersContext();
  const posthog = usePostHog();
  const { shipment: selectedShipment, error: fetchShipmentError } =
    useShipment<CarrierCondensedShipment>({ shipmentId });

  const handlePageChange = (
    _e: React.ChangeEvent<unknown>,
    value: number,
  ): void => {
    setPage(value);
  };

  const handleFiltersOpen = (): void => {
    setFiltersOpen(true);
    // Track filter button click with comprehensive context
    posthog?.capture(ClientAppEvent.CARRIER_FILTER_BUTTON_CLICKED, {
      current_page: AppPage.SHIPMENT_DETAIL,
      shipment_id: selectedShipment?.info?.id ?? null,
      active_filters_count: getActiveFiltersCount(filters),
      applied_filters: {
        equipment_types: filters?.equipment_types ?? [],
        services: filters?.services ?? [],
        commodity: filters?.commodity?.code ?? null,
        status: filters?.status ?? null,
        order_by: filters?.order_by ?? null,
        origin_provinces: filters?.origin?.provinces ?? [],
        origin_countries: filters?.origin?.countries ?? [],
        destination_provinces: filters?.destination?.provinces ?? [],
        destination_countries: filters?.destination?.countries ?? [],
        shipment_name: filters?.shipmentName ?? null,
        shipment_relationship: filters?.shipmentRelationship ?? null,
      },
    });
  };

  const handleFiltersClose = (): void => {
    setFiltersOpen(false);
  };

  const refreshShipment = useCallback(() => {
    // Refreshing the list too is intended here: the user asked for a refresh.
    revalidateShipmentDetailAndListCaches(revalidateAll);
  }, [revalidateAll]);

  return (
    <CarrierShipmentPageProvider
      shipment={selectedShipment}
      onRefresh={refreshShipment}
      error={fetchShipmentError}
    >
      <Shipment
        shipments={shipments as CarrierCondensedShipment[]}
        shipmentSelectionIsDisabled={shipmentsAreLoading}
        shipmentOverview={<ShipmentOverviewExpanded />}
        page={page}
        pages={pages}
        handlePageSelected={handlePageChange}
        shipmentCount={count}
        onFiltersClick={handleFiltersOpen}
      />
      <Dialog
        open={filtersOpen}
        onClose={handleFiltersClose}
        maxWidth="md"
        fullWidth
        aria-label="Shipment filters"
      >
        <DialogContent>
          <ShipmentsFilter
            onShipmentSearchCreated={handleFiltersClose}
            currentPage={AppPage.SHIPMENT_DETAIL}
          />
        </DialogContent>
      </Dialog>
      <ShipmentLiveUpdateController shipmentId={shipmentId} role={role} />
    </CarrierShipmentPageProvider>
  );
}
